import { v4 as uuidv4 } from 'uuid';
import { getAllSegments, getClipFPS } from '@goldcast/player-config/src/utils/clipUtils';
import { getSegmentsTrack } from '@goldcast/player-config/src/utils/playerConfigGetters';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { Segment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/Segment';
import { Element } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/Element';
import { ElementsTrack } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Track/ElementsTrack';
import { TrackType } from '@goldcast/player-config/src/types/Track/Type';
import { approxGTE, approxLTE } from '@goldcast/player-config/src/utils/compareUtils';
import { getClipTotalDurationInFrames } from '@/App/remotion/MainPlayer/utils/configUtils';

const MAX_ELEMENT_TRACKS = 50;
interface SegmentBounds {
  id: string;
  fromFrame: number;
  toFrame: number;
}

interface ElementWithinSegment {
  element: Element;
  trackId: string;
  trackIndex: number;
  elementIndex: number;
  overlapType: 'full' | 'partial-start' | 'partial-end' | 'partial-both';
  overlapFromFrame: number;
  overlapToFrame: number;
}

interface ElementWithTrackInfo {
  element: Element;
  originalTrackId: string;
}

const getElementsTracks = (clip: Clip): ElementsTrack[] => {
  const playerConfig = clip.asset_metadata.player_config;
  if (!playerConfig) {
    return [];
  }
  return playerConfig.playerConfig.tracks.filter(track => track.type === TrackType.ELEMENTS) as ElementsTrack[];
};

const getSegmentBounds = (segments: Segment[]): SegmentBounds[] => {
  return segments.map(segment => ({
    id: segment.id,
    fromFrame: segment.fromFrame,
    toFrame: segment.toFrame
  }));
};

const hasTimeOverlap = (
  element1: { fromFrame: number; toFrame: number },
  element2: { fromFrame: number; toFrame: number }
): boolean => {
  return !(approxLTE(element1.toFrame, element2.fromFrame) || approxLTE(element2.toFrame, element1.fromFrame));
};

const canFitInTrack = (element: { fromFrame: number; toFrame: number }, trackElements: Element[]): boolean => {
  return !trackElements.some(existingElement => hasTimeOverlap(existingElement, element));
};

const createNewElementsTrack = (): ElementsTrack => {
  const trackId = uuidv4();
  return {
    id: trackId,
    name: `Track - ${trackId}`,
    type: TrackType.ELEMENTS,
    hideTrack: { inTimeline: false, inPlayer: false },
    elements: []
  };
};

const findOrCreateTrackForElement = (
  element: Element,
  tracks: ElementsTrack[],
  preferredTrackId?: string
): { track: ElementsTrack; isNew: boolean } => {
  if (preferredTrackId) {
    const preferredTrack = tracks.find(t => t.id === preferredTrackId);
    if (preferredTrack && canFitInTrack(element, preferredTrack.elements)) {
      return { track: preferredTrack, isNew: false };
    }
  }

  for (const track of tracks) {
    if (canFitInTrack(element, track.elements)) {
      return { track, isNew: false };
    }
  }

  const newTrack = createNewElementsTrack();
  if (tracks.length > MAX_ELEMENT_TRACKS) {
    window.logger?.error('Too many tracks', { trackCount: tracks.length });
  }
  tracks.push(newTrack);
  return { track: newTrack, isNew: true };
};

const findElementsWithinSegment = (
  clip: Clip,
  segmentFromFrame: number,
  segmentToFrame: number
): ElementWithinSegment[] => {
  const elementsTracks = getElementsTracks(clip);
  const totalDuration = getClipTotalDurationInFrames(clip);
  const elementsWithinSegment: ElementWithinSegment[] = [];

  elementsTracks.forEach((track, trackIndex) => {
    track.elements.forEach((element, elementIndex) => {
      const elementDuration = element.toFrame - element.fromFrame;
      const spansMoreThan50Percent = elementDuration > totalDuration * 0.5;

      if (spansMoreThan50Percent) {
        return;
      }

      const overlapStart = Math.max(element.fromFrame, segmentFromFrame);
      const overlapEnd = Math.min(element.toFrame, segmentToFrame);

      if (approxGTE(overlapStart, overlapEnd)) {
        return;
      }

      let overlapType: ElementWithinSegment['overlapType'];

      const startsWithinSegment = approxGTE(element.fromFrame, segmentFromFrame);
      const endsWithinSegment = approxLTE(element.toFrame, segmentToFrame);

      if (startsWithinSegment && endsWithinSegment) {
        overlapType = 'full';
      } else if (!startsWithinSegment && !endsWithinSegment) {
        overlapType = 'partial-both';
      } else if (!startsWithinSegment) {
        overlapType = 'partial-start';
      } else {
        overlapType = 'partial-end';
      }

      elementsWithinSegment.push({
        element,
        trackId: track.id,
        trackIndex,
        elementIndex,
        overlapType,
        overlapFromFrame: overlapStart,
        overlapToFrame: overlapEnd
      });
    });
  });

  return elementsWithinSegment;
};

const duplicateElement = (element: Element): Element => {
  return {
    ...structuredClone(element),
    id: uuidv4()
  };
};

const splitElementForSegment = (
  elementInfo: ElementWithinSegment,
  segmentFromFrame: number,
  segmentToFrame: number
): { movedPart: ElementWithTrackInfo; remainingParts: ElementWithTrackInfo[] } => {
  const { element, overlapType, overlapFromFrame, overlapToFrame, trackId } = elementInfo;

  const movedPart = duplicateElement(element);
  movedPart.fromFrame = overlapFromFrame;
  movedPart.toFrame = overlapToFrame;

  const remainingParts: ElementWithTrackInfo[] = [];

  if (overlapType === 'partial-start' || overlapType === 'partial-both') {
    const leftPart = duplicateElement(element);
    leftPart.fromFrame = element.fromFrame;
    leftPart.toFrame = segmentFromFrame;
    if (leftPart.toFrame > leftPart.fromFrame) {
      remainingParts.push({ element: leftPart, originalTrackId: trackId });
    }
  }

  if (overlapType === 'partial-end' || overlapType === 'partial-both') {
    const rightPart = duplicateElement(element);
    rightPart.fromFrame = segmentToFrame;
    rightPart.toFrame = element.toFrame;
    if (rightPart.toFrame > rightPart.fromFrame) {
      remainingParts.push({ element: rightPart, originalTrackId: trackId });
    }
  }

  return { movedPart: { element: movedPart, originalTrackId: trackId }, remainingParts };
};

/**
 * Re-projects an overlay element onto the reordered segment layout. The element's overlap with each old
 * segment is remapped to that segment's new position; sub-ranges whose segments stayed adjacent are merged
 * back into one element, while sub-ranges whose segments were separated by the reorder become independent
 * pieces. This prevents a callout that spans two segments from stretching across an unrelated segment that
 * the reorder inserted between them. An element that overlaps no surviving segment is returned unchanged.
 */
const reprojectElementAcrossSegments = (
  element: Element,
  oldSegmentBounds: SegmentBounds[],
  newSegmentBounds: SegmentBounds[]
): Element[] => {
  const remappedRanges: { fromFrame: number; toFrame: number }[] = [];

  oldSegmentBounds.forEach(oldBounds => {
    const newBounds = newSegmentBounds.find(b => b.id === oldBounds.id);
    if (!newBounds) {
      return;
    }

    const overlapStart = Math.max(element.fromFrame, oldBounds.fromFrame);
    const overlapEnd = Math.min(element.toFrame, oldBounds.toFrame);
    if (approxGTE(overlapStart, overlapEnd)) {
      return;
    }

    const shift = newBounds.fromFrame - oldBounds.fromFrame;
    remappedRanges.push({ fromFrame: overlapStart + shift, toFrame: overlapEnd + shift });
  });

  if (remappedRanges.length === 0) {
    return [element];
  }

  // Merge sub-ranges that stayed contiguous in the new timeline; a gap means the reorder separated them.
  remappedRanges.sort((a, b) => a.fromFrame - b.fromFrame);
  const mergedRanges: { fromFrame: number; toFrame: number }[] = [];
  remappedRanges.forEach(range => {
    const previous = mergedRanges[mergedRanges.length - 1];
    if (previous && approxGTE(previous.toFrame, range.fromFrame)) {
      previous.toFrame = Math.max(previous.toFrame, range.toFrame);
    } else {
      mergedRanges.push({ ...range });
    }
  });

  // The first piece keeps the original element's identity; any additional pieces are fresh duplicates.
  return mergedRanges.map((range, index) => ({
    ...(index === 0 ? element : duplicateElement(element)),
    fromFrame: range.fromFrame,
    toFrame: range.toFrame
  }));
};

const updateElementTimingsWithTrackAssignment = (
  elementsTracks: ElementsTrack[],
  elementsToMove: Map<string, ElementWithTrackInfo>,
  remainingElements: Map<string, ElementWithTrackInfo[]>,
  oldSegmentBounds: SegmentBounds[],
  newSegmentBounds: SegmentBounds[],
  activeOldBounds: SegmentBounds,
  activeNewBounds: SegmentBounds,
  totalDurationFrames: number
): ElementsTrack[] => {
  const updatedTracks: ElementsTrack[] = structuredClone(elementsTracks);
  const elementsToReassign: { element: Element; preferredTrackId: string }[] = [];

  updatedTracks.forEach(track => {
    const newElements: Element[] = [];

    const placeElement = (element: Element, preferredTrackId: string) => {
      if (canFitInTrack(element, newElements)) {
        newElements.push(element);
      } else {
        elementsToReassign.push({ element, preferredTrackId });
      }
    };

    track.elements.forEach(element => {
      if (elementsToMove.has(element.id)) {
        const movedInfo = elementsToMove.get(element.id)!;
        const movedElement = movedInfo.element;
        const offsetWithinActiveSegment = movedElement.fromFrame - activeOldBounds.fromFrame;
        const newFromFrame = activeNewBounds.fromFrame + offsetWithinActiveSegment;
        const duration = movedElement.toFrame - movedElement.fromFrame;

        placeElement({ ...movedElement, fromFrame: newFromFrame, toFrame: newFromFrame + duration }, track.id);

        const remaining = remainingElements.get(element.id);
        if (remaining) {
          remaining.forEach(remainingInfo => {
            reprojectElementAcrossSegments(remainingInfo.element, oldSegmentBounds, newSegmentBounds).forEach(piece =>
              placeElement(piece, remainingInfo.originalTrackId)
            );
          });
        }
      } else {
        const elementDuration = element.toFrame - element.fromFrame;
        const spansMoreThan50Percent = elementDuration > totalDurationFrames * 0.5;

        if (spansMoreThan50Percent) {
          placeElement({ ...element }, track.id);
        } else {
          reprojectElementAcrossSegments(element, oldSegmentBounds, newSegmentBounds).forEach(piece =>
            placeElement(piece, track.id)
          );
        }
      }
    });

    track.elements = newElements.filter(el => el.toFrame > el.fromFrame && approxGTE(el.fromFrame, 0));
  });

  elementsToReassign.forEach(({ element, preferredTrackId }) => {
    if (approxLTE(element.toFrame, element.fromFrame) || element.fromFrame < 0) {
      return;
    }

    const { track } = findOrCreateTrackForElement(element, updatedTracks, preferredTrackId);
    track.elements.push(element);
  });

  return updatedTracks.filter(track => track.elements.length > 0);
};

// A partially-overlapped overlay (e.g. a callout used across splits) is trimmed rather than left
// untouched. If its surviving remnant is shorter than this it is removed entirely, since a callout
// needs a beat of screen time to be readable.
const MINIMUM_CALLOUT_SURVIVING_DURATION_SEC = 0.2;

/**
 * Adjusts a single overlay element for the deletion of the frame range [segmentFromFrame, segmentToFrame].
 * Returns the adjusted element, or null if it should be dropped (fully inside the deleted range, or a
 * trimmed remnant shorter than the minimum surviving duration). Elements that are only shifted (fully
 * after the range) keep their full duration and are never dropped by the minimum-duration rule.
 */
const adjustOverlayForDeletedSegment = (
  element: Element,
  segmentFromFrame: number,
  segmentToFrame: number,
  minSurvivingFrames: number
): Element | null => {
  const removedDuration = segmentToFrame - segmentFromFrame;
  const { fromFrame, toFrame } = element;

  // Fully before the deleted range: unchanged.
  if (approxLTE(toFrame, segmentFromFrame)) {
    return element;
  }

  // Fully after the deleted range: shift left, duration preserved.
  if (approxGTE(fromFrame, segmentToFrame)) {
    return { ...element, fromFrame: fromFrame - removedDuration, toFrame: toFrame - removedDuration };
  }

  // Fully inside the deleted range: removed.
  if (approxGTE(fromFrame, segmentFromFrame) && approxLTE(toFrame, segmentToFrame)) {
    return null;
  }

  // Partial overlap: excise the overlapping portion. Any surviving right-hand part shifts left to the
  // cut point (segmentFromFrame), so when the element spans the whole range its two ends join with no gap.
  const startsBeforeCut = !approxGTE(fromFrame, segmentFromFrame);
  const endsAfterCut = !approxLTE(toFrame, segmentToFrame);
  const newFromFrame = startsBeforeCut ? fromFrame : segmentFromFrame;
  const newToFrame = endsAfterCut ? toFrame - removedDuration : segmentFromFrame;

  // Drop remnants that are too short to be useful.
  if (!approxGTE(newToFrame - newFromFrame, minSurvivingFrames)) {
    return null;
  }

  return { ...element, fromFrame: newFromFrame, toFrame: newToFrame };
};

export const removeOverlaysForDeletedSegment = (clip: Clip, segmentFromFrame: number, segmentToFrame: number): void => {
  const playerConfig = clip.asset_metadata.player_config;
  if (!playerConfig) {
    return;
  }

  const minSurvivingFrames = MINIMUM_CALLOUT_SURVIVING_DURATION_SEC * getClipFPS(clip);
  const elementsTracks = getElementsTracks(clip);
  const updatedTracks = elementsTracks
    .map(track => ({
      ...track,
      elements: track.elements
        .map(el => adjustOverlayForDeletedSegment(el, segmentFromFrame, segmentToFrame, minSurvivingFrames))
        .filter((el): el is Element => el !== null)
    }))
    .filter(track => track.elements.length > 0);

  const nonElementTracks = playerConfig.playerConfig.tracks.filter(t => t.type !== TrackType.ELEMENTS);
  playerConfig.playerConfig.tracks = [...nonElementTracks, ...updatedTracks];
};

export const shiftOverlaysAfterSegment = (clip: Clip, segmentOldToFrame: number, durationDeltaFrames: number): void => {
  if (durationDeltaFrames === 0) {
    return;
  }

  const playerConfig = clip.asset_metadata.player_config;
  if (!playerConfig) {
    return;
  }

  const elementsTracks = getElementsTracks(clip);

  const updatedTracks = elementsTracks
    .map(track => ({
      ...track,
      elements: track.elements.map(el => {
        if (approxGTE(el.fromFrame, segmentOldToFrame)) {
          return { ...el, fromFrame: el.fromFrame + durationDeltaFrames, toFrame: el.toFrame + durationDeltaFrames };
        }
        return el;
      })
    }))
    .filter(track => track.elements.length > 0);

  const nonElementTracks = playerConfig.playerConfig.tracks.filter(t => t.type !== TrackType.ELEMENTS);
  playerConfig.playerConfig.tracks = [...nonElementTracks, ...updatedTracks];
};

export const reorderSegment = ({
  newClip,
  activeId,
  overId
}: {
  newClip: Clip;
  activeId: string;
  overId: string;
}): void => {
  const playerConfig = newClip.asset_metadata.player_config;
  if (!playerConfig) {
    return;
  }

  const allSegments = getAllSegments(newClip);
  const activeIndex = allSegments.findIndex(segment => segment.id === activeId);
  const overIndex = allSegments.findIndex(segment => segment.id === overId);

  if (activeIndex === -1 || overIndex === -1 || activeIndex === overIndex) {
    return;
  }

  const activeSegment = allSegments[activeIndex];
  const oldSegmentBounds = getSegmentBounds(allSegments);
  const activeOldBounds = oldSegmentBounds[activeIndex];

  const elementsWithinActive = findElementsWithinSegment(newClip, activeSegment.fromFrame, activeSegment.toFrame);

  const elementsToMove = new Map<string, ElementWithTrackInfo>();
  const remainingElements = new Map<string, ElementWithTrackInfo[]>();

  elementsWithinActive.forEach(elementInfo => {
    if (elementInfo.overlapType === 'full') {
      elementsToMove.set(elementInfo.element.id, {
        element: elementInfo.element,
        originalTrackId: elementInfo.trackId
      });
    } else {
      const { movedPart, remainingParts } = splitElementForSegment(
        elementInfo,
        activeSegment.fromFrame,
        activeSegment.toFrame
      );
      elementsToMove.set(elementInfo.element.id, movedPart);
      if (remainingParts.length > 0) {
        remainingElements.set(elementInfo.element.id, remainingParts);
      }
    }
  });

  const segmentsTrack = getSegmentsTrack(playerConfig);
  const [removed] = segmentsTrack.segments.splice(activeIndex, 1);
  segmentsTrack.segments.splice(overIndex, 0, removed);

  let currentFrame = 0;
  const newSegmentBounds = getSegmentBounds(segmentsTrack.segments);
  newSegmentBounds.forEach(segment => {
    const duration = segment.toFrame - segment.fromFrame;
    segment.fromFrame = currentFrame;
    segment.toFrame = currentFrame + duration;
    currentFrame += duration;
  });

  const activeNewBounds = newSegmentBounds.find(b => b.id === activeId)!;

  const elementsTracks = getElementsTracks(newClip);
  const totalDurationFrames = getClipTotalDurationInFrames(newClip);
  const updatedElementsTracks = updateElementTimingsWithTrackAssignment(
    elementsTracks,
    elementsToMove,
    remainingElements,
    oldSegmentBounds,
    newSegmentBounds,
    activeOldBounds,
    activeNewBounds,
    totalDurationFrames
  );

  const nonElementTracks = playerConfig.playerConfig.tracks.filter(track => track.type !== TrackType.ELEMENTS);
  playerConfig.playerConfig.tracks = [...nonElementTracks, ...updatedElementsTracks];
};
